import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { ArrowLeft, FileText, TrendingUp, AlertCircle, Eye, Mail, FolderArchive } from "lucide-react";
import Layout from "../components/Layout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getStatusBadgeClass = (status) => {
  const classes = {
    "Activo": "badge-activo",
    "Vencido": "badge-vencido",
    "Próximo a vencer": "badge-proximo",
    "Por comenzar": "badge-por-comenzar",
    "Renovado": "badge-renovado",
    "Archivado": "badge-archivado"
  };
  return `badge ${classes[status] || ""}`;
};

const formatCurrency = (amount, currency = "MXN") => {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: currency
  }).format(amount);
};

const formatDate = (dateString) => {
  // Parse date string without timezone conversion
  const [year, month, day] = dateString.split('T')[0].split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  return date.toLocaleDateString('es-MX');
};

export default function OwnerDetail({ user, onLogout }) {
  const { email } = useParams();
  const [ownerData, setOwnerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("activos");
  const navigate = useNavigate();

  useEffect(() => {
    fetchOwnerData();
  }, [email]);

  const fetchOwnerData = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/owners/${encodeURIComponent(email)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOwnerData(response.data);
    } catch (error) {
      toast.error("Error al cargar datos del propietario");
      navigate("/propietarios");
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvitation = async () => {
    if (!window.confirm(`¿Enviar invitación a ${email}?`)) return;

    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/invitations/send?propietario_email=${encodeURIComponent(email)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Invitación enviada exitosamente");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar la invitación");
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Cargando...</div>
        </div>
      </Layout>
    );
  }

  if (!ownerData) return null;

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6 fade-in">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900" data-testid="owner-name">
              {ownerData.propietario_nombre}
            </h1>
            <p className="text-gray-500 mt-1">{ownerData.propietario_email}</p>
            <p className="text-gray-500">{ownerData.propietario_telefono}</p>
          </div>
          <div className="flex gap-2">
            {(user.rol === "admin" || user.rol === "maestro") && (
              <Button onClick={handleSendInvitation} data-testid="send-invitation-button">
                <Mail className="w-4 h-4 mr-2" />
                Enviar Invitación
              </Button>
            )}
            <Button variant="outline" onClick={() => navigate("/propietarios")} data-testid="back-to-owners-button">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card data-testid="owner-stat-total">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Total Contratos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="text-2xl font-bold">{ownerData.total_contratos}</div>
                <FileText className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card data-testid="owner-stat-activos">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Activos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="text-2xl font-bold text-green-600">{ownerData.contratos_activos}</div>
                <TrendingUp className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card data-testid="owner-stat-por-vencer">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Por Vencer</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="text-2xl font-bold text-orange-600">{ownerData.contratos_por_vencer}</div>
                <AlertCircle className="w-8 h-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>

          <Card data-testid="owner-stat-renta">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Renta Total</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-600">
                {formatCurrency(ownerData.renta_mensual_total)}
              </div>
              <p className="text-xs text-gray-500 mt-1">mensual</p>
            </CardContent>
          </Card>
        </div>

        {/* Contracts Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="activos" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Contratos ({ownerData.contratos.filter(c => c.estatus !== "Archivado").length})
            </TabsTrigger>
            <TabsTrigger value="archivados" className="flex items-center gap-2">
              <FolderArchive className="w-4 h-4" />
              Archivados ({ownerData.contratos.filter(c => c.estatus === "Archivado").length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>{activeTab === "activos" ? "Contratos" : "Contratos Archivados"}</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="owner-contracts-table">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Número
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Inquilino
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Inmueble
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Renta
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Término
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Estado
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {ownerData.contratos
                        .filter(c => activeTab === "activos" ? c.estatus !== "Archivado" : c.estatus === "Archivado")
                        .length === 0 ? (
                        <tr>
                          <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                            {activeTab === "activos" 
                              ? "No hay contratos activos" 
                              : "No hay contratos archivados"}
                          </td>
                        </tr>
                      ) : (
                        ownerData.contratos
                          .filter(c => activeTab === "activos" ? c.estatus !== "Archivado" : c.estatus === "Archivado")
                          .map((contract) => (
                          <tr key={contract.id} className="hover:bg-gray-50" data-testid={`owner-contract-${contract.numero_contrato}`}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {contract.numero_contrato}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {contract.inquilino_nombre}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900">
                              {contract.inmueble_nombre}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatCurrency(contract.renta_mensual, contract.moneda)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatDate(contract.fecha_termino)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={getStatusBadgeClass(contract.estatus)}>
                                {contract.estatus}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(`/contratos/${contract.id}`)}
                                data-testid={`view-contract-${contract.numero_contrato}`}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
